import { useCallback } from 'react';
import type { SubscriptionBannerVariant } from 'src/components/SubscriptionBanner/SubscriptionBanner.enums';
import { getAnalytics } from 'src/lib/analytics/analytics.service';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { SUBSCRIPTION_EVENTS } from './subscriptionAnalytics.constants';

/**
 * Union of every valid subscription-domain analytics event name.
 */
export type tSubscriptionEventName =
  (typeof SUBSCRIPTION_EVENTS)[keyof typeof SUBSCRIPTION_EVENTS];

/**
 * Domain hook that emits subscription analytics events through the neutral
 * adapter (`getAnalytics`). Consumers never talk to the PostHog SDK directly.
 *
 * Auto-injects `workspace_id`, `user_role` and `email` when available so
 * per-event payloads never carry their own copy; `email` is intentionally
 * emitted on every subscription event. Unlike `useAnalytics` it does not drop
 * the event when the workspace is missing: it omits the absent key instead.
 *
 * @returns An object exposing `trackSubscriptionEvent` to emit the events.
 */
/**
 * The CTA surface that triggered `front.global.click.purchase_cta`.
 * The first three values come verbatim from the product Event bible (`modal`
 * is the SubscribeIntegrationsModal button, `banner` the SubscriptionBanner
 * button, `button` the Navbar trial button); the remaining surfaces keep the
 * value each one already emitted as its legacy `cta_type`.
 */
export type tPurchaseCta =
  | 'modal'
  | 'banner'
  | 'button'
  | 'ImportLeads'
  | 'AddUsers'
  | SubscriptionBannerVariant;

/**
 * The `cta_type` entry-point value of `front.global.click.purchase_cta`.
 * Values predate this hook and are referenced by saved dashboards; the
 * `banner` value replaces the banner button's previous per-page variant
 * values, a historical split confirmed by product in the Event bible. The
 * variant values remain valid on the legacy banner-body click.
 */
export type tPurchaseCtaType =
  | 'header'
  | 'banner'
  | 'ConnectIntegrations'
  | 'ImportLeads'
  | 'AddUsers'
  | SubscriptionBannerVariant;

export function useSubscriptionAnalytics() {
  const { currentUser, currentUserRole } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  const trackSubscriptionEvent = useCallback(
    (name: tSubscriptionEventName, payload: Record<string, unknown> = {}) => {
      getAnalytics().track(name, {
        ...(currentWorkspaceId && { workspace_id: currentWorkspaceId }),
        ...(currentUserRole && { user_role: currentUserRole }),
        ...(currentUser?.email && { email: currentUser.email }),
        ...payload,
      });
    },
    [currentWorkspaceId, currentUserRole, currentUser?.email],
  );

  // Single owner of the purchase CTA payload so the six surfaces that emit it
  // never drift on copy. `cta` is the surface, `cta_type` the prod-live legacy
  // entry-point value.
  const trackPurchaseCta = useCallback(
    (cta: tPurchaseCta, ctaType: tPurchaseCtaType) => {
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PURCHASE_CTA, {
        display_name: 'Clicks en CTAs',
        description:
          'Cuántas usuarias dan click a algún banner, modal o CTA que lleve a compra',
        cta,
        cta_type: ctaType,
      });
    },
    [trackSubscriptionEvent],
  );

  return { trackSubscriptionEvent, trackPurchaseCta };
}
